import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Tab,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Verified as VerifyIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Paid as PayIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, CurrencyPicker, CustomerPicker, PurchaseInvoicePicker, SupplierPicker } from '../LedgerPickers';
import {
  WhtCertStatusChip,
  WhtRemitStatusChip,
  whtCertIdOf,
  whtRemitIdOf,
  fmtDate,
  getErrorMessage,
  unwrap,
} from './TaxShared';

const CERT_TYPES = ['PAYABLE', 'RECEIVABLE'];
const CERT_STATUSES = ['PENDING', 'ISSUED', 'SUBMITTED', 'VERIFIED', 'EXPIRED', 'CANCELLED'];
const REMIT_STATUSES = ['PENDING', 'SUBMITTED', 'PAID', 'CONFIRMED', 'REJECTED'];

const emptyCert = () => ({
  certificate_number: '',
  certificate_type: 'PAYABLE',
  customer_id: '',
  supplier_id: '',
  party_name: '',
  party_tin: '',
  currency_id: '',
  issue_date: new Date().toISOString().slice(0, 10),
  tax_period_start: '',
  tax_period_end: '',
  basis_amount: '',
  wht_rate: '6',
  wht_amount: '',
  authority_ref: '',
  notes: '',
});

const emptyRemit = () => ({
  remittance_number: '',
  remittance_type: 'PAYABLE',
  currency_id: '',
  tax_period_start: '',
  tax_period_end: '',
  remittance_date: '',
  total_wht_amount: '',
  total_basis_amount: '',
  authority_ref: '',
  notes: '',
  certificate_ids: '',
});

// Withholding tax (verified against WHT controllers/DTOs): certificates go
// PENDING -> ISSUED -> VERIFIED (or CANCELLED) and can be raised straight
// from a supplier invoice; remittances bundle certificates and flow PENDING
// -> SUBMITTED -> CONFIRMED -> PAID, settling via post from a bank account.
const WhtTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.TAX, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.TAX, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [tab, setTab] = useState('certs');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCert, setShowCert] = useState(false);
  const [certValues, setCertValues] = useState(emptyCert());
  const [saving, setSaving] = useState(false);
  const [certDetail, setCertDetail] = useState(null);
  const [showRemit, setShowRemit] = useState(false);
  const [remitValues, setRemitValues] = useState(emptyRemit());
  const [remitDetail, setRemitDetail] = useState(null);
  const [postDlg, setPostDlg] = useState({ open: false, item: null, settlement_account_id: '', payment_date: new Date().toISOString().slice(0, 10), payment_reference: '' });
  const [linkDlg, setLinkDlg] = useState({ open: false, item: null, certificate_ids: '' });
  const [fromInvDlg, setFromInvDlg] = useState({ open: false, invoice_id: '', certificate_number: '', wht_rate: '6', issue_date: '', notes: '' });
  const [summary, setSummary] = useState(null);
  const [summaryFilters, setSummaryFilters] = useState({ from_date: '', to_date: '', type: '' });
  const [summaryLoading, setSummaryLoading] = useState(false);

  const reload = () => setRefreshKey((k) => k + 1);

  const runWorkflow = (label, pastTense, fn, after) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()}?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`${label} successful`.replace(/^./, (c) => c.toUpperCase()).replace('successful', pastTense), 'success');
          after?.();
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const submitCert = async () => {
    const v = certValues;
    if (!v.certificate_number.trim() || !v.party_name.trim() || !v.currency_id || !v.issue_date || !v.tax_period_start || !v.tax_period_end) {
      showSnackbar('Certificate number, party, currency, issue date and tax period are required', 'error');
      return;
    }
    if (!(Number(v.basis_amount) > 0) || !(Number(v.wht_rate) >= 0) || !(Number(v.wht_amount) >= 0)) {
      showSnackbar('Basis amount (>0), WHT rate and WHT amount (>=0) are required', 'error');
      return;
    }
    try {
      setSaving(true);
      await ledgerAPI.createWhtCertificate({
        certificate_number: v.certificate_number.trim(),
        certificate_type: v.certificate_type,
        ...(v.customer_id ? { customer_id: Number(v.customer_id) } : {}),
        ...(v.supplier_id ? { supplier_id: Number(v.supplier_id) } : {}),
        party_name: v.party_name.trim(),
        ...(v.party_tin?.trim() ? { party_tin: v.party_tin.trim() } : {}),
        currency_id: Number(v.currency_id),
        issue_date: v.issue_date,
        tax_period_start: v.tax_period_start,
        tax_period_end: v.tax_period_end,
        basis_amount: Number(v.basis_amount),
        wht_rate: Number(v.wht_rate),
        wht_amount: Number(v.wht_amount),
        ...(v.authority_ref?.trim() ? { authority_ref: v.authority_ref.trim() } : {}),
        ...(v.notes?.trim() ? { notes: v.notes.trim() } : {}),
      });
      showSnackbar('WHT certificate created as PENDING', 'success');
      setShowCert(false);
      setCertValues(emptyCert());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitFromInvoice = async () => {
    if (!fromInvDlg.invoice_id || !fromInvDlg.certificate_number.trim() || !(Number(fromInvDlg.wht_rate) >= 0)) {
      showSnackbar('Supplier invoice, certificate number and WHT rate are required', 'error');
      return;
    }
    try {
      await ledgerAPI.createWhtCertificateFromInvoice(fromInvDlg.invoice_id, {
        certificate_number: fromInvDlg.certificate_number.trim(),
        wht_rate: Number(fromInvDlg.wht_rate),
        ...(fromInvDlg.issue_date ? { issue_date: fromInvDlg.issue_date } : {}),
        ...(fromInvDlg.notes?.trim() ? { notes: fromInvDlg.notes.trim() } : {}),
      });
      showSnackbar('Certificate raised from supplier invoice', 'success');
      setFromInvDlg({ open: false, invoice_id: '', certificate_number: '', wht_rate: '6', issue_date: '', notes: '' });
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const openCertDetail = async (row) => {
    try {
      const res = await ledgerAPI.getWhtCertificate(whtCertIdOf(row));
      setCertDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch certificate'), 'error');
    }
  };

  const submitRemit = async () => {
    const v = remitValues;
    if (!v.remittance_number.trim() || !v.currency_id || !v.tax_period_start || !v.tax_period_end) {
      showSnackbar('Remittance number, currency and tax period are required', 'error');
      return;
    }
    if (!(Number(v.total_wht_amount) >= 0) || !(Number(v.total_basis_amount) >= 0)) {
      showSnackbar('WHT and basis totals must be zero or more', 'error');
      return;
    }
    const certIds = String(v.certificate_ids || '').split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => Number.isInteger(n) && n > 0);
    try {
      setSaving(true);
      await ledgerAPI.createWhtRemittance({
        remittance_number: v.remittance_number.trim(),
        remittance_type: v.remittance_type,
        currency_id: Number(v.currency_id),
        tax_period_start: v.tax_period_start,
        tax_period_end: v.tax_period_end,
        ...(v.remittance_date ? { remittance_date: v.remittance_date } : {}),
        total_wht_amount: Number(v.total_wht_amount),
        total_basis_amount: Number(v.total_basis_amount),
        ...(v.authority_ref?.trim() ? { authority_ref: v.authority_ref.trim() } : {}),
        ...(v.notes?.trim() ? { notes: v.notes.trim() } : {}),
        ...(certIds.length ? { certificate_ids: certIds } : {}),
      });
      showSnackbar('WHT remittance created as PENDING', 'success');
      setShowRemit(false);
      setRemitValues(emptyRemit());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openRemitDetail = async (row) => {
    try {
      const res = await ledgerAPI.getWhtRemittance(whtRemitIdOf(row));
      setRemitDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch remittance'), 'error');
    }
  };

  const submitPost = async () => {
    if (!postDlg.settlement_account_id) {
      showSnackbar('Settlement bank/cash account is required', 'error');
      return;
    }
    try {
      await ledgerAPI.postWhtRemittance(whtRemitIdOf(postDlg.item), {
        settlement_account_id: Number(postDlg.settlement_account_id),
        ...(postDlg.payment_date ? { payment_date: postDlg.payment_date } : {}),
        ...(postDlg.payment_reference?.trim() ? { payment_reference: postDlg.payment_reference.trim() } : {}),
      });
      showSnackbar('Remittance posted', 'success');
      setPostDlg({ open: false, item: null, settlement_account_id: '', payment_date: '', payment_reference: '' });
      setRemitDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitLink = async () => {
    const ids = String(linkDlg.certificate_ids || '').split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => Number.isInteger(n) && n > 0);
    if (!ids.length) {
      showSnackbar('Enter at least one certificate ID', 'error');
      return;
    }
    try {
      await ledgerAPI.linkWhtCertificates(whtRemitIdOf(linkDlg.item), { certificate_ids: ids });
      showSnackbar('Certificates linked', 'success');
      setLinkDlg({ open: false, item: null, certificate_ids: '' });
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const runSummary = async () => {
    if (!summaryFilters.from_date || !summaryFilters.to_date) {
      showSnackbar('From and to dates are required', 'error');
      return;
    }
    try {
      setSummaryLoading(true);
      const res = await ledgerAPI.getWhtSummary({
        from_date: summaryFilters.from_date,
        to_date: summaryFilters.to_date,
        ...(summaryFilters.type ? { type: summaryFilters.type } : {}),
      });
      setSummary(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch WHT summary'), 'error');
    } finally {
      setSummaryLoading(false);
    }
  };

  const summaryRows = summary ? (Array.isArray(summary.rows) ? summary.rows : (Array.isArray(summary) ? summary : [])) : [];

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '8px !important', pt: '4px !important' }}>
          <Tabs value={tab} onChange={(_, v) => v && setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab value="certs" label="Certificates" />
            <Tab value="remits" label="Remittances" />
            <Tab value="summary" label="Summary" />
          </Tabs>
        </CardContent>
      </Card>

      {tab === 'certs' && (
        <LedgerListPage
          resource={RESOURCES.LEDGER.TAX}
          title="WHT Certificates"
          subtitle="Withholding deducted from suppliers (PAYABLE) or suffered on sales (RECEIVABLE)"
          addLabel="New Certificate"
          idKey="certificate_id"
          searchPlaceholder="Search number, party, TIN..."
          emptyMessage="No WHT certificates found."
          refreshKey={refreshKey}
          fetchItems={(params) => ledgerAPI.getWhtCertificates(params)}
          onAdd={canCreate ? () => { setCertValues(emptyCert()); setShowCert(true); } : null}
          extraActions={canCreate ? [{ label: 'From Supplier Invoice', variant: 'outlined', onClick: () => setFromInvDlg({ open: true, invoice_id: '', certificate_number: '', wht_rate: '6', issue_date: '', notes: '' }) }] : []}
          filters={[
            { key: 'status', label: 'Status', options: CERT_STATUSES.map((s) => ({ label: s, value: s })) },
            { key: 'certificate_type', label: 'Type', options: CERT_TYPES.map((s) => ({ label: s, value: s })) },
            { type: 'picker', key: 'supplier_id', label: 'Supplier', picker: SupplierPicker },
            { type: 'picker', key: 'customer_id', label: 'Customer', picker: CustomerPicker },
            { type: 'daterange', keyFrom: 'from_date', keyTo: 'to_date', label: 'Issue date' },
          ]}
          columns={[
            { label: 'Certificate #', render: (c) => <strong>{c.certificate_number || `WHT-${whtCertIdOf(c)}`}</strong> },
            { label: 'Party', render: (c) => c.party_name || '-' },
            { label: 'Basis', render: (c) => formatAccountingAmount(c.basis_amount), align: 'right', hideOnMobile: true },
            { label: 'WHT', render: (c) => formatAccountingAmount(c.wht_amount), align: 'right' },
            { label: 'Status', render: (c) => <WhtCertStatusChip status={c.status} /> },
          ]}
          rowActions={(c) => {
            const id = whtCertIdOf(c);
            const status = String(c.status || 'PENDING').toUpperCase();
            const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openCertDetail(c) }];
            if (status === 'PENDING' && canApprove) {
              actions.push({ icon: ApproveIcon, title: 'Issue', color: 'primary', onClick: () => runWorkflow('Issue certificate', 'issued', () => ledgerAPI.issueWhtCertificate(id), () => openCertDetail(c)) });
            }
            if (status === 'ISSUED' && canApprove) {
              actions.push({ icon: VerifyIcon, title: 'Verify', color: 'primary', onClick: () => runWorkflow('Verify certificate', 'verified', () => ledgerAPI.verifyWhtCertificate(id), () => openCertDetail(c)) });
            }
            if (!['CANCELLED', 'VERIFIED'].includes(status) && canApprove) {
              actions.push({ icon: CancelIcon, title: 'Cancel', color: 'error', onClick: () => runWorkflow('Cancel certificate', 'cancelled', () => ledgerAPI.cancelWhtCertificate(id)) });
            }
            return actions;
          }}
        />
      )}

      {tab === 'remits' && (
        <LedgerListPage
          resource={RESOURCES.LEDGER.TAX}
          title="WHT Remittances"
          subtitle="Bundled certificates filed and paid to the authority"
          addLabel="New Remittance"
          idKey="remittance_id"
          searchPlaceholder="Search remittance #..."
          emptyMessage="No WHT remittances found."
          refreshKey={refreshKey}
          fetchItems={(params) => ledgerAPI.getWhtRemittances(params)}
          onAdd={canCreate ? () => { setRemitValues(emptyRemit()); setShowRemit(true); } : null}
          filters={[
            { key: 'status', label: 'Status', options: REMIT_STATUSES.map((s) => ({ label: s, value: s })) },
            { key: 'remittance_type', label: 'Type', options: CERT_TYPES.map((s) => ({ label: s, value: s })) },
            { type: 'daterange', keyFrom: 'from_date', keyTo: 'to_date', label: 'Period' },
          ]}
          columns={[
            { label: 'Remittance #', render: (r) => <strong>{r.remittance_number || `WHTR-${whtRemitIdOf(r)}`}</strong> },
            { label: 'Period', render: (r) => `${fmtDate(r.tax_period_start)} - ${fmtDate(r.tax_period_end)}`, hideOnMobile: true },
            { label: 'WHT Total', render: (r) => formatAccountingAmount(r.total_wht_amount), align: 'right' },
            { label: 'Status', render: (r) => <WhtRemitStatusChip status={r.status} /> },
          ]}
          rowActions={(r) => {
            const id = whtRemitIdOf(r);
            const status = String(r.status || 'PENDING').toUpperCase();
            const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openRemitDetail(r) }];
            if (canApprove) {
              actions.push({ icon: LinkIcon, title: 'Link certificates', color: 'default', onClick: () => setLinkDlg({ open: true, item: r, certificate_ids: '' }) });
            }
            if (status === 'PENDING' && canApprove) {
              actions.push({ icon: ApproveIcon, title: 'Submit', color: 'primary', onClick: () => runWorkflow('Submit remittance', 'submitted', () => ledgerAPI.submitWhtRemittance(id)) });
            }
            if (status === 'SUBMITTED' && canApprove) {
              actions.push({ icon: VerifyIcon, title: 'Confirm', color: 'primary', onClick: () => runWorkflow('Confirm remittance', 'confirmed', () => ledgerAPI.confirmWhtRemittance(id)) });
            }
            if (status === 'CONFIRMED' && canApprove) {
              actions.push({ icon: PayIcon, title: 'Mark paid', color: 'primary', onClick: () => runWorkflow('Mark remittance paid', 'marked as paid', () => ledgerAPI.markWhtRemittancePaid(id)) });
              actions.push({ icon: PostIcon, title: 'Post (settle)', color: 'primary', onClick: () => setPostDlg({ open: true, item: r, settlement_account_id: '', payment_date: new Date().toISOString().slice(0, 10), payment_reference: '' }) });
            }
            return actions;
          }}
        />
      )}

      {tab === 'summary' && (
        <Card>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth size="small" type="date" label="From" value={summaryFilters.from_date} onChange={(e) => setSummaryFilters((p) => ({ ...p, from_date: e.target.value }))} InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth size="small" type="date" label="To" value={summaryFilters.to_date} onChange={(e) => setSummaryFilters((p) => ({ ...p, to_date: e.target.value }))} InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth size="small" select label="Type" value={summaryFilters.type} onChange={(e) => setSummaryFilters((p) => ({ ...p, type: e.target.value }))}>
                  <MenuItem value="">Both</MenuItem>
                  {CERT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }} display="flex" alignItems="center">
                <Button variant="contained" onClick={runSummary} disabled={summaryLoading}>
                  {summaryLoading ? 'Loading...' : 'Run Summary'}
                </Button>
              </Grid>
            </Grid>
            {!summary ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>Choose a period and run the summary.</Typography>
            ) : summaryRows.length ? (
              <DataTable
                columns={['Item', 'Certificates', 'WHT Amount', 'Basis']}
                data={summaryRows}
                loading={false}
                emptyMessage="No rows."
                renderRow={(r, i) => (
                  <TableRow key={i}>
                    <TableCell><strong>{r.label || r.certificate_type || r.period || `Row ${i + 1}`}</strong></TableCell>
                    <TableCell align="right">{r.certificate_count ?? r.count ?? '-'}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(r.wht_amount ?? r.total_wht)}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(r.basis_amount ?? r.total_basis)}</TableCell>
                  </TableRow>
                )}
              />
            ) : (
              <Box>
                {Object.entries(summary.totals ?? summary)
                  .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
                  .slice(0, 12)
                  .map(([k, v]) => (
                    <Typography key={k} variant="body2">
                      {k.replace(/_/g, ' ')}: <strong>{typeof v === 'number' ? formatAccountingAmount(v) : v}</strong>
                    </Typography>
                  ))}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={showCert} onClose={() => setShowCert(false)} maxWidth="md" fullWidth>
        <DialogTitle>New WHT Certificate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Certificate Number" value={certValues.certificate_number} onChange={(e) => setCertValues((p) => ({ ...p, certificate_number: e.target.value }))} required />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" select label="Type" value={certValues.certificate_type} onChange={(e) => setCertValues((p) => ({ ...p, certificate_type: e.target.value }))}>
                {CERT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="Issue Date" value={certValues.issue_date} onChange={(e) => setCertValues((p) => ({ ...p, issue_date: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Party Name" value={certValues.party_name} onChange={(e) => setCertValues((p) => ({ ...p, party_name: e.target.value }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Party TIN" value={certValues.party_tin} onChange={(e) => setCertValues((p) => ({ ...p, party_tin: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SupplierPicker value={certValues.supplier_id} onChange={(id) => setCertValues((p) => ({ ...p, supplier_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomerPicker value={certValues.customer_id} onChange={(id) => setCertValues((p) => ({ ...p, customer_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CurrencyPicker value={certValues.currency_id} onChange={(id) => setCertValues((p) => ({ ...p, currency_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField fullWidth size="small" type="date" label="Tax Period Start" value={certValues.tax_period_start} onChange={(e) => setCertValues((p) => ({ ...p, tax_period_start: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField fullWidth size="small" type="date" label="Tax Period End" value={certValues.tax_period_end} onChange={(e) => setCertValues((p) => ({ ...p, tax_period_end: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField fullWidth size="small" type="number" label="Basis Amount" value={certValues.basis_amount} onChange={(e) => setCertValues((p) => ({ ...p, basis_amount: e.target.value }))} inputProps={{ min: 0.01, step: 'any' }} required />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField fullWidth size="small" type="number" label="WHT Rate %" value={certValues.wht_rate} onChange={(e) => setCertValues((p) => ({ ...p, wht_rate: e.target.value, wht_amount: e.target.value !== '' && certValues.basis_amount !== '' ? String(Math.round(Number(certValues.basis_amount) * Number(e.target.value)) / 100) : p.wht_amount }))} inputProps={{ min: 0, max: 100, step: 'any' }} required />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField fullWidth size="small" type="number" label="WHT Amount" value={certValues.wht_amount} onChange={(e) => setCertValues((p) => ({ ...p, wht_amount: e.target.value }))} inputProps={{ min: 0, step: 'any' }} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Authority Ref" value={certValues.authority_ref} onChange={(e) => setCertValues((p) => ({ ...p, authority_ref: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Notes" value={certValues.notes} onChange={(e) => setCertValues((p) => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCert(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCert} disabled={saving}>
            {saving ? 'Saving...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fromInvDlg.open} onClose={() => setFromInvDlg({ open: false, invoice_id: '', certificate_number: '', wht_rate: '6', issue_date: '', notes: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Raise Certificate From Supplier Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <PurchaseInvoicePicker value={fromInvDlg.invoice_id} onChange={(id) => setFromInvDlg((p) => ({ ...p, invoice_id: id }))} required />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" label="Certificate Number" value={fromInvDlg.certificate_number} onChange={(e) => setFromInvDlg((p) => ({ ...p, certificate_number: e.target.value }))} required />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" type="number" label="WHT Rate %" value={fromInvDlg.wht_rate} onChange={(e) => setFromInvDlg((p) => ({ ...p, wht_rate: e.target.value }))} inputProps={{ min: 0, max: 100, step: 'any' }} required />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" type="date" label="Issue Date (defaults to invoice date)" value={fromInvDlg.issue_date} onChange={(e) => setFromInvDlg((p) => ({ ...p, issue_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Notes" value={fromInvDlg.notes} onChange={(e) => setFromInvDlg((p) => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFromInvDlg({ open: false, invoice_id: '', certificate_number: '', wht_rate: '6', issue_date: '', notes: '' })}>Cancel</Button>
          <Button variant="contained" onClick={submitFromInvoice}>Raise Certificate</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showRemit} onClose={() => setShowRemit(false)} maxWidth="md" fullWidth>
        <DialogTitle>New WHT Remittance</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Remittance Number" value={remitValues.remittance_number} onChange={(e) => setRemitValues((p) => ({ ...p, remittance_number: e.target.value }))} required />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" select label="Type" value={remitValues.remittance_type} onChange={(e) => setRemitValues((p) => ({ ...p, remittance_type: e.target.value }))}>
                {CERT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <CurrencyPicker value={remitValues.currency_id} onChange={(id) => setRemitValues((p) => ({ ...p, currency_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="Tax Period Start" value={remitValues.tax_period_start} onChange={(e) => setRemitValues((p) => ({ ...p, tax_period_start: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="Tax Period End" value={remitValues.tax_period_end} onChange={(e) => setRemitValues((p) => ({ ...p, tax_period_end: e.target.value }))} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="Remittance Date" value={remitValues.remittance_date} onChange={(e) => setRemitValues((p) => ({ ...p, remittance_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="number" label="Total WHT" value={remitValues.total_wht_amount} onChange={(e) => setRemitValues((p) => ({ ...p, total_wht_amount: e.target.value }))} inputProps={{ min: 0, step: 'any' }} required />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="number" label="Total Basis" value={remitValues.total_basis_amount} onChange={(e) => setRemitValues((p) => ({ ...p, total_basis_amount: e.target.value }))} inputProps={{ min: 0, step: 'any' }} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Certificate IDs (comma-separated, optional)" value={remitValues.certificate_ids} onChange={(e) => setRemitValues((p) => ({ ...p, certificate_ids: e.target.value }))} placeholder="e.g. 12, 15" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Authority Ref" value={remitValues.authority_ref} onChange={(e) => setRemitValues((p) => ({ ...p, authority_ref: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Notes" value={remitValues.notes} onChange={(e) => setRemitValues((p) => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRemit(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitRemit} disabled={saving}>
            {saving ? 'Saving...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!certDetail} onClose={() => setCertDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Certificate {certDetail?.certificate_number || ''}</DialogTitle>
        <DialogContent>
          {certDetail && (
            <>
              <Typography variant="body2">{certDetail.party_name} {certDetail.party_tin ? `(${certDetail.party_tin})` : ''}</Typography>
              <Typography variant="body2" color="text.secondary">Basis {formatAccountingAmount(certDetail.basis_amount)} @ {certDetail.wht_rate}% = {formatAccountingAmount(certDetail.wht_amount)}</Typography>
              <Typography variant="body2" color="text.secondary">Period {fmtDate(certDetail.tax_period_start)} - {fmtDate(certDetail.tax_period_end)}</Typography>
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                <WhtCertStatusChip status={certDetail.status} />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCertDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!remitDetail} onClose={() => setRemitDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Remittance {remitDetail?.remittance_number || ''}</DialogTitle>
        <DialogContent>
          {remitDetail && (
            <>
              <Typography variant="body2" color="text.secondary">Period {fmtDate(remitDetail.tax_period_start)} - {fmtDate(remitDetail.tax_period_end)}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>WHT total: <strong>{formatAccountingAmount(remitDetail.total_wht_amount)}</strong></Typography>
              <Typography variant="body2">Basis total: <strong>{formatAccountingAmount(remitDetail.total_basis_amount)}</strong></Typography>
              {Array.isArray(remitDetail.certificates) && remitDetail.certificates.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>{remitDetail.certificates.length} certificate(s) linked</Typography>
              )}
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                <WhtRemitStatusChip status={remitDetail.status} />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemitDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={postDlg.open}
        onClose={() => setPostDlg({ open: false, item: null, settlement_account_id: '', payment_date: '', payment_reference: '' })}
        title="Post remittance (settle)"
        onSubmit={submitPost}
        values={postDlg}
        onChange={(key, value) => setPostDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[
          { key: 'settlement_account_id', label: 'Settlement Bank/Cash Account', type: 'picker', picker: AccountPicker, required: true, md: 12 },
          { key: 'payment_date', label: 'Payment Date (defaults to today)', type: 'date', md: 6 },
          { key: 'payment_reference', label: 'Payment Reference', md: 6 },
        ]}
      />

      <LedgerFormDialog
        open={linkDlg.open}
        onClose={() => setLinkDlg({ open: false, item: null, certificate_ids: '' })}
        title="Link certificates to remittance"
        onSubmit={submitLink}
        values={linkDlg}
        onChange={(key, value) => setLinkDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'certificate_ids', label: 'Certificate IDs (comma-separated)', required: true, helper: 'e.g. 12, 15, 21', md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
};

export default WhtTab;